import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class TokenService {
  private readonly secretKey = 'educanet-secure-key-2024'; // In production, use environment variable

  constructor() {}

  /**
   * Encrypts student and album IDs into a secure token
   */
  generateToken(studentId: string, albumId: string, albumName: string): string {
    const data = `${studentId}:${albumId}:${albumName}:${Date.now()}`;
    return this.encrypt(data);
  }

  /**
   * Decrypts a token to extract student and album IDs
   */
  decryptToken(
    token: string,
  ): { studentId: string; albumId: string; albumName: string } | null {
    try {
      const decrypted = this.decrypt(token);
      const parts = decrypted.split(':');

      if (parts.length >= 3) {
        // maintenant on attend au moins 3 parties
        return {
          studentId: parts[0],
          albumId: parts[1],
          albumName: parts[2], // ajout du nom de l'album
        };
      }
      return null;
    } catch (error) {
      console.error('Error decrypting token:', error);
      return null;
    }
  }

  /**
   * Simple encryption using Base64 encoding with obfuscation
   * Note: For production, consider using a more robust encryption library
   */
  private encrypt(text: string): string {
    // Simple obfuscation: reverse string, add random chars, then base64
    const reversed = text.split('').reverse().join('');
    const obfuscated = this.addRandomChars(reversed);
    return btoa(obfuscated).replace(/[+/=]/g, (match) => {
      switch (match) {
        case '+':
          return '-';
        case '/':
          return '_';
        case '=':
          return '';
        default:
          return match;
      }
    });
  }

  /**
   * Simple decryption
   */
  private decrypt(encryptedText: string): string {
    // Reverse the URL-safe base64 encoding
    const base64 = encryptedText.replace(/[-_]/g, (match) => {
      return match === '-' ? '+' : '/';
    });

    // Add padding if needed
    const padded = base64 + '==='.slice(0, (4 - (base64.length % 4)) % 4);

    const decoded = atob(padded);
    const deobfuscated = this.removeRandomChars(decoded);
    return deobfuscated.split('').reverse().join('');
  }

  /**
   * Add random characters for obfuscation
   */
  private addRandomChars(text: string): string {
    const chars =
      'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';

    for (let i = 0; i < text.length; i++) {
      result += text[i];
      if (i % 3 === 0) {
        result += chars[Math.floor(Math.random() * chars.length)];
      }
    }

    return result;
  }

  /**
   * Remove random characters added during obfuscation
   */
  private removeRandomChars(text: string): string {
    let result = '';

    for (let i = 0; i < text.length; i++) {
      result += text[i];
      if (i % 4 === 0 && i > 0) {
        i++; // Skip the random character
      }
    }

    return result;
  }

  /**
   * Validates if a token is properly formatted
   */
  isValidToken(token: string): boolean {
    const decrypted = this.decryptToken(token);
    return decrypted !== null && !!decrypted.studentId && !!decrypted.albumId;
  }
}
